import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, Button, Modal, Popconfirm, Space, Table } from 'antd';
import {
  MenuOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';
import {
  fetchActionCatalog,
  deleteAction,
  reorderActions,
  clearActionCatalogError
} from '../../../redux/actions/trm/actionCatalog';
import { fetchAMLSAlertLevel } from '../../../redux/actions/common';
import ActionFormModal from './ActionFormModal';

// TODO(FIN-11255 follow-up): drag handle is mouse-only — fails WCAG 2.1.1.
// Add explicit "Move up / Move down" buttons or migrate to a keyboard-accessible
// drag library (e.g. @dnd-kit/sortable) before public release.
const DragHandle = SortableHandle(({ disabled }) => (
  <MenuOutlined
    style={{
      cursor: disabled ? 'not-allowed' : 'grab',
      color: disabled ? '#ccc' : '#999'
    }}
  />
));

const SortableRow = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);

// FIN-13289 — `source` is the table the reference was found in. Show what the
// user would call that screen; the row id follows in brackets for support.
const SOURCE_LABELS = {
  typology: 'Typology',
  scenario_typology: 'Scenario'
};
const sourceLabel = (source) => SOURCE_LABELS[source] || source;

const arrayMove = (arr, from, to) => {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

class ActionCatalog extends React.Component {
  state = { modalVisible: false, editing: null };

  componentDidMount() {
    this.props.fetchActionCatalog();
    // FIN-12133: severity display names come from alert_level_details
    // (alertLevelClassification). Ensure they're loaded to resolve the stored
    // action_severity code -> display name in the table.
    if (
      typeof this.props.fetchAMLSAlertLevel === 'function' &&
      (!Array.isArray(this.props.alertLevels) ||
        this.props.alertLevels.length === 0)
    ) {
      this.props.fetchAMLSAlertLevel();
    }
  }

  // Resolve a stored severity code to its display name (case-insensitive),
  // mirroring the code<->displayName convention used elsewhere.
  severityLabel = (code) => {
    if (!code) return '—';
    const match = (this.props.alertLevels || []).find(
      (lvl) => (lvl.code || '').toLowerCase() === String(code).toLowerCase()
    );
    return match?.displayName || code;
  };

  errorTitle = (error) => {
    if (Array.isArray(error?.usedBy) && error.usedBy.length > 0) {
      return 'Cannot delete or rename — action is referenced';
    }
    return 'Action catalog error';
  };

  errorContent = (error) => {
    if (!error) return null;
    if (Array.isArray(error.usedBy) && error.usedBy.length > 0) {
      return (
        <div>
          <p>This action is referenced by the following rules:</p>
          {/* FIN-13290 — bound the list so a long reference set scrolls in
              place instead of pushing the dialog's buttons off screen. */}
          <ul style={{ maxHeight: 240, overflowY: 'auto' }}>
            {error.usedBy.map((u, i) => (
              <li key={`${u.source}-${u.ownerId}-${i}`}>
                <strong>{sourceLabel(u.source)}</strong>
                {u.ruleName ? ` — ${u.ruleName}` : ''}
                {u.ownerId !== undefined ? ` (#${u.ownerId})` : ''}
              </li>
            ))}
          </ul>
          <p>Remove or rename references before deleting/renaming.</p>
        </div>
      );
    }
    if (error.message) return <div>{error.message}</div>;
    return <pre>{JSON.stringify(error, null, 2)}</pre>;
  };

  getSortedRows = () => {
    const { rows } = this.props;
    return [...rows]
      .map((r, idx) => ({
        ...r,
        sortOrder: typeof r.sortOrder === 'number' ? r.sortOrder : idx + 1
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    // Gate during in-flight requests to prevent drag/delete races
    if (this.props.loading) return;
    const sorted = this.getSortedRows();
    const next = arrayMove(sorted, oldIndex, newIndex);
    const entries = next.map((r, i) => ({
      displayName: r.displayName,
      sortOrder: i + 1
    }));
    this.props.reorderActions(entries);
  };

  openAdd = () => this.setState({ modalVisible: true, editing: null });
  openEdit = (record) => this.setState({ modalVisible: true, editing: record });
  closeModal = () => this.setState({ modalVisible: false, editing: null });

  buildColumns = () => [
    {
      title: '',
      dataIndex: 'sortHandle',
      width: 40,
      render: () => <DragHandle disabled={!!this.props.loading} />
    },
    { title: 'Name', dataIndex: 'displayName', key: 'displayName' },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      render: (w) => (typeof w === 'number' ? w : Number(w))
    },
    {
      title: 'Severity',
      dataIndex: 'actionSeverity',
      key: 'actionSeverity',
      render: (code) => this.severityLabel(code)
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (d) => d || '—'
    },
    { title: 'Order', dataIndex: 'sortOrder', key: 'sortOrder' },
    {
      title: 'Actions',
      key: 'rowActions',
      width: 160,
      render: (_text, record) => (
        <Space size="middle">
          <a onClick={() => this.openEdit(record)}>
            <EditOutlined /> Edit
          </a>
          <Popconfirm
            title={`Delete ${record.displayName}?`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => this.props.deleteAction(record.displayName)}>
            <a style={{ color: '#cf1322' }}>
              <DeleteOutlined /> Delete
            </a>
          </Popconfirm>
        </Space>
      )
    }
  ];

  draggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={this.handleSortEnd}
      {...props}
    />
  );

  draggableBodyRow = ({ ...restProps }) => {
    const sorted = this.getSortedRows();
    const index = sorted.findIndex(
      (x) => x.displayName === restProps['data-row-key']
    );
    return <SortableRow index={index} {...restProps} />;
  };

  render() {
    const { loading, error, clearActionCatalogError } = this.props;
    const { modalVisible, editing } = this.state;
    const sorted = this.getSortedRows();

    return (
      <div style={{ padding: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Action Catalog changes take effect on the next Run Instance execution (weights are read fresh at run time — no scenario re-save needed). For the real-time pipeline, they apply when the challenger is rebuilt on the next precompute / champion-update run."
        />
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={this.openAdd}>
            Add Action
          </Button>
        </div>
        <Table
          rowKey="displayName"
          columns={this.buildColumns()}
          dataSource={sorted}
          loading={loading}
          pagination={false}
          components={{
            body: {
              wrapper: this.draggableContainer,
              row: this.draggableBodyRow
            }
          }}
        />
        <ActionFormModal
          visible={modalVisible}
          editing={editing}
          onClose={this.closeModal}
        />
        <Modal
          visible={!!error}
          onOk={clearActionCatalogError}
          onCancel={clearActionCatalogError}
          title={this.errorTitle(error)}>
          {this.errorContent(error)}
        </Modal>
      </div>
    );
  }
}

ActionCatalog.propTypes = {
  rows: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.object,
  alertLevels: PropTypes.array,
  fetchActionCatalog: PropTypes.func.isRequired,
  fetchAMLSAlertLevel: PropTypes.func,
  deleteAction: PropTypes.func.isRequired,
  reorderActions: PropTypes.func.isRequired,
  clearActionCatalogError: PropTypes.func.isRequired
};

ActionCatalog.defaultProps = {
  rows: [],
  loading: false,
  error: null,
  alertLevels: [],
  fetchAMLSAlertLevel: undefined
};

const mapStateToProps = (state) => ({
  rows: state.actionCatalog?.rows || [],
  loading: state.actionCatalog?.loading || false,
  error: state.actionCatalog?.error || null,
  alertLevels: state.amlsCommon?.alertLevelConfigs?.data || []
});

export default connect(mapStateToProps, {
  fetchActionCatalog,
  fetchAMLSAlertLevel,
  deleteAction,
  reorderActions,
  clearActionCatalogError
})(ActionCatalog);
